import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth = async () => {
      // Проверяем, действительно ли это Telegram WebApp с данными
      const isTelegramWebApp = window.Telegram?.WebApp?.initData && 
                               window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
      
      if (isTelegramWebApp) {
        const tg = window.Telegram.WebApp;
        tg.ready();
        tg.expand();
        tg.enableClosingConfirmation();

        if (tg.colorScheme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }

        // Пользователь вышел вручную → НЕ авто-логинимся (иначе кнопка «Выйти»
        // бессмысленна: initData всегда даёт повторный вход). Показываем экран входа.
        if (localStorage.getItem('admin_logged_out') === '1') {
          setUser(null);
          setLoading(false);
          return;
        }

        try {
          const u = tg.initDataUnsafe.user;
          const body = {
            userId: u.id,
            username: u.username || '',
            firstName: u.first_name || '',
            lastName: u.last_name || '',
            initData: tg.initData
          };
          const resp = await api.post('/auth/login', body);
          const { token: newToken, user: userData } = resp.data;
          localStorage.removeItem('admin_logged_out');
          localStorage.setItem('token', newToken);
          setToken(newToken);
          setUser(userData);
          setError(null);
        } catch (e) {
          setUser(null);
          setError(e?.response?.data?.message || 'Ошибка аутентификации через Telegram WebApp');
        } finally {
          setLoading(false);
        }
        return;
      }

      // Обычный режим: проверяем существующий токен
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data.user);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (initData) => {
    try {
      const tg = window.Telegram?.WebApp;
      const payload = tg?.initData || initData || '';
      const u = tg?.initDataUnsafe?.user;
      const body = u ? {
        userId: u.id,
        username: u.username || '',
        firstName: u.first_name || '',
        lastName: u.last_name || '',
        initData: payload
      } : { initData: payload };
      const response = await api.post('/auth/login', body);
      const { token: newToken, user: userData } = response.data;
      localStorage.removeItem('admin_logged_out');
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      setError(null);
      return { success: true };
    } catch (error) {
      const msg = error.response?.data?.message || 'Ошибка входа';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  const requestLogin = async () => {
    try {
      const response = await api.post('/auth/request-login');
      return {
        success: true,
        requestId: response.data.requestId,
        code: response.data.code,
        botUrl: response.data.botUrl,
        expiresAt: response.data.expiresAt,
      };
    } catch (error) {
      const msg = error.response?.data?.message || 'Не удалось отправить запрос на вход';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  const checkLoginStatus = async (requestId) => {
    try {
      const response = await api.get(`/auth/login-status/${requestId}`);
      const { status } = response.data;
      if (status === 'approved') {
        const { token: newToken, user: userData } = response.data;
        localStorage.removeItem('admin_logged_out');
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
        setError(null);
      }
      return { status };
    } catch (error) {
      return { status: 'error', error: error.response?.data?.message };
    }
  };

  const logout = async () => {
    // Флаг блокирует авто-логин по Telegram initData (в initAuth/ProtectedRoute/Login),
    // иначе выход мгновенно отменяется повторным входом.
    localStorage.setItem('admin_logged_out', '1');
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setError(null);

    // Всегда уводим на экран входа (вход через бота) — там можно войти ДРУГИМ
    // аккаунтом. close() НЕ зовём: в Telegram Web он no-op, а на Desktop закрыл бы
    // окно вместо показа входа.
    navigate('/login');
  };

  const value = {
    user,
    loading,
    error,
    login,
    requestLogin,
    checkLoginStatus,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
